import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Logo } from '@/components/ui/logo';
import { updatePassword } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isHoofdhart, setIsHoofdhart] = useState(false);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function init() {
      // Check if the hash fragment contains the access_token
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || '';

      if (accessToken) {
        await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
      }

      // Check user role for branding
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roleData } = await supabase
          .from('profile_role')
          .select('role')
          .eq('user_id', user.id)
          .single();
        if (roleData?.role === 'hoofdhartuser') setIsHoofdhart(true);
      }
      setReady(true);
    }
    init();
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    if (password.length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens bevatten');
      return;
    }

    setLoading(true);

    try {
      await updatePassword(password);
      setMessage('Wachtwoord succesvol bijgewerkt');
      setTimeout(() => {
        navigate(isHoofdhart ? '/hoofdhartlogin' : '/login');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!ready) {
    return <div className="min-h-screen bg-black" />;
  }

  const backgroundImage = isHoofdhart ? '/images/hoofdnaarhart-bg.png' : '/Background.png';
  // the logo sits inside the card, on a light surface, so both variants use
  // their normal artwork. logotrans.svg rather than the default logo.svg:
  // that one carries an opaque white box, which shows as a patch against the
  // card's translucent background.
  const logoSrc = isHoofdhart
    ? '/images/hoofdnaarhart-logo.png'
    : '/images/logotrans.svg';
  const buttonClass = isHoofdhart
    ? 'w-full rounded-full bg-red-700 hover:bg-red-600 text-white'
    : 'w-full rounded-full bg-green-800 hover:bg-green-700 text-white';
  const loginPath = isHoofdhart ? '/hoofdhartlogin' : '/login';

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 relative cap-login"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30" />

      <div className="relative z-10 w-full max-w-md">
        <Card className="w-full max-w-md p-6 space-y-6 bg-white/80 backdrop-blur-md shadow-xl text-slate-900">
          <Logo className="w-48 h-48 mx-auto" src={logoSrc} />

          <div className="space-y-2 text-center">
            <h2 className="text-2xl font-bold">Wachtwoord resetten</h2>
            <p className="text-slate-600">
              Voer je nieuwe wachtwoord in
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nieuw wachtwoord</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bevestig wachtwoord</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}
            {message && <p className="text-sm text-green-500">{message}</p>}

            <Button
              type="submit"
              className={buttonClass}
              disabled={loading}
            >
              {loading ? 'Laden...' : 'Wachtwoord bijwerken'}
            </Button>

            <div className="text-center">
              <Button
                variant="link"
                onClick={() => navigate(loginPath)}
                className="text-sm text-slate-900"
              >
                Terug naar inloggen
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
